import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { clsxm } from '@vs-shop/utils';

import { renderThrough, type RenderElement } from '../../internal/renderedPart.tsx';

/**
 * One announcement, on the thin bar above the page: a mark, a headline, the
 * message, and — where the shop wants one — a chip that reads as somewhere to
 * go.
 *
 *   <AlertMessage render={<Link href={link} />}>
 *     {icon}
 *     <AlertMessageBody>
 *       <AlertMessageText variant={AlertMessageTextVariant.UNDERLINED}>
 *         <AlertMessageTitle>{title}: </AlertMessageTitle>
 *         <span>{message}</span>
 *       </AlertMessageText>
 *       <AlertMessageBadge>
 *         <AlertMessageBadgeLabel>{buttonText}</AlertMessageBadgeLabel>
 *         <ChevronRight />
 *       </AlertMessageBadge>
 *     </AlertMessageBody>
 *   </AlertMessage>
 *
 * **A message without a link is not a link.** The row renders a neutral element
 * and takes an anchor through `render` when the message carries a destination —
 * which is also what decides whether the copy underlines under a pointer, and
 * why that is an appearance the composition names rather than something the row
 * infers. An anchor with an empty `href` resolves to the page it is already on,
 * so pressing it reloads.
 *
 * **Nothing about the message crosses.** The mark is drawn by the tenant and
 * sized here; the headline, the copy and the chip's label arrive as children;
 * which message is current, when it expires and whether it was dismissed are
 * all the shop's.
 */

/**
 * The row. `group` is what the copy's hover underline resolves against, so it
 * is the row's whether or not the row ends up being a link.
 */
const alertMessageClasses =
  'group lap-lg:gap-2 lap-lg:[&>svg]:size-5 flex items-center gap-1 [&>svg]:size-4 [&>svg]:shrink-0';

export type AlertMessageProps = ComponentProps<'div'> & {
  /** The element to paint instead of the neutral `<div>` — an anchor, where the message goes somewhere. */
  readonly render?: RenderElement;
};

/** One announcement row. */
export const AlertMessage = ({ className, render, ...props }: AlertMessageProps) => {
  const resolved = clsxm(alertMessageClasses, className);

  return (
    renderThrough(render, 'alert-message', resolved, props) ?? (
      <div data-slot="alert-message" className={resolved} {...props} />
    )
  );
};

/** The copy and the chip beside it — wrapping where the bar is narrow. */
const alertMessageBodyClasses = 'lap:flex-nowrap flex flex-wrap gap-2';

export type AlertMessageBodyProps = ComponentProps<'div'>;

/** Everything in the row except the mark. */
export const AlertMessageBody = ({ className, ...props }: AlertMessageBodyProps) => (
  <div
    data-slot="alert-message-body"
    className={clsxm(alertMessageBodyClasses, className)}
    {...props}
  />
);

/**
 * The copy. One line from `lap` up, whatever its length — the bar is 40px and a
 * message that wrapped would push the row open.
 */
export const alertMessageTextVariants = cva(
  'lap:line-clamp-1 lap-lg:text-sm/5 text-xs/4 tracking-tight',
  {
    variants: {
      variant: {
        /** Underlines with the row under a pointer — for a row that goes somewhere. */
        underlined: 'group-hover:underline group-hover:underline-offset-2',
        /** Reads as copy and stays that way. */
        plain: '',
      },
    },
  }
);

type AlertMessageTextVariantName = NonNullable<
  VariantProps<typeof alertMessageTextVariants>['variant']
>;

export type AlertMessageTextVariant = AlertMessageTextVariantName;

/** `satisfies` binds these to the recipe above, so the two cannot drift. */
export const AlertMessageTextVariant = {
  /** Underlines with the row. */
  UNDERLINED: 'underlined',
  /** Stays copy. */
  PLAIN: 'plain',
} as const satisfies Record<string, AlertMessageTextVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type AlertMessageTextVariantProps = { readonly variant: AlertMessageTextVariant };

export type AlertMessageTextProps = ComponentProps<'div'> & AlertMessageTextVariantProps;

/** The message copy. */
export const AlertMessageText = ({ className, variant, ...props }: AlertMessageTextProps) => (
  <div
    data-slot="alert-message-text"
    className={clsxm(alertMessageTextVariants({ variant }), className)}
    {...props}
  />
);

/**
 * The headline in front of the copy. Sits inside the copy rather than beside
 * it, and is separated by a space rather than a margin, so the row's underline
 * runs unbroken across both.
 */
const alertMessageTitleClasses = 'lap:font-black font-semibold';

export type AlertMessageTitleProps = ComponentProps<'span'>;

/** The headline. */
export const AlertMessageTitle = ({ className, ...props }: AlertMessageTitleProps) => (
  <span
    data-slot="alert-message-title"
    className={clsxm(alertMessageTitleClasses, className)}
    {...props}
  />
);

/**
 * The message itself, after the headline. Reads a shade heavier from `lap` up,
 * where the bar has the room for it — the whole line is small type on a busy
 * band, and the copy is the part of it that has to survive a glance.
 */
const alertMessageCopyClasses = 'lap:font-semibold';

export type AlertMessageCopyProps = ComponentProps<'span'>;

/** The message copy after the headline. */
export const AlertMessageCopy = ({ className, ...props }: AlertMessageCopyProps) => (
  <span
    data-slot="alert-message-copy"
    className={clsxm(alertMessageCopyClasses, className)}
    {...props}
  />
);

/**
 * The chip at the end of the row: not a control, and deliberately not shaped
 * like one — the whole row is already the click surface, and a button inside a
 * link is a second target for the same destination.
 */
const alertMessageBadgeClasses =
  'border-foreground/20 lap-lg:[&>svg]:size-4 flex items-center rounded-sm border px-1 font-normal whitespace-nowrap [&>svg]:size-3 [&>svg]:shrink-0 [&>svg]:opacity-50';

export type AlertMessageBadgeProps = ComponentProps<'div'>;

/** The chip at the end of the row. */
export const AlertMessageBadge = ({ className, ...props }: AlertMessageBadgeProps) => (
  <div
    data-slot="alert-message-badge"
    className={clsxm(alertMessageBadgeClasses, className)}
    {...props}
  />
);

/** The chip's label, one step below the copy beside it. */
const alertMessageBadgeLabelClasses = 'text-xs/4';

export type AlertMessageBadgeLabelProps = ComponentProps<'span'>;

/** The chip's label. */
export const AlertMessageBadgeLabel = ({ className, ...props }: AlertMessageBadgeLabelProps) => (
  <span
    data-slot="alert-message-badge-label"
    className={clsxm(alertMessageBadgeLabelClasses, className)}
    {...props}
  />
);
